import {
  DeleteOutlined,
  LinkOutlined,
  PlayCircleOutlined,
} from "@ant-design/icons";
import {
  Button,
  Empty,
  Form,
  Input,
  Modal,
  Popconfirm,
  Spin,
  Tooltip,
  message,
} from "antd";
import axios from "axios";
import React, { useState } from "react";
import { BsPencil } from "react-icons/bs";
import { useQuery, useQueryClient } from "react-query";
import { serverUrl } from "../../../../nestserver";
import { ADMIN_GUIDES, INSTRUCTOR_VIDEO } from "./helpGuides";

const toLines = (text) =>
  String(text || "")
    .split(/\r?\n/)
    .filter(Boolean);

const isInstructorFaq = (rec) =>
  /instructor/i.test(`${rec?.question || ""} ${rec?.answer || ""}`);

export default function HelpPage() {
  const [form] = Form.useForm();

  const queryClient = useQueryClient();

  const [help, setHelp] = useState(null);
  const [editHelp, setEditHelp] = useState(null);
  const [playingId, setPlayingId] = useState(null);
  const [expanded, setExpanded] = useState([]);

  const copyLink = (video) => {
    const url = window.location.origin + video;
    navigator.clipboard
      .writeText(url)
      .then(() => message.success("Video link copied"))
      .catch(() => message.error("Could not copy link"));
  };

  const toggleMore = (id) =>
    setExpanded((ids) =>
      ids.includes(id) ? ids.filter((i) => i !== id) : [...ids, id]
    );

  async function getHelp() {
    const res = await axios({
      method: "get",
      url: serverUrl + "/help/",
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
      },
    });

    return res.data;
  }

  const helps = useQuery("help", getHelp);

  // Operations

  function createHelp(values) {
    setHelp(false);
    axios
      .post(serverUrl + "/help", values, {
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
        },
      })
      .then((res) => {
        message.success("Help Created");
        form.resetFields();
        queryClient.invalidateQueries("help");
      })
      .catch((error) => {
        message.error(error?.response?.data?.message);
      });
  }

  // Delete Help
  function deleteHelp(id) {
    axios
      .delete(serverUrl + "/help/" + id, {
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
        },
      })
      .then((res) => {
        message.success("Help Deleted");
        queryClient.invalidateQueries("help");
      });
  }

  // Update Help
  function updateHelp(values) {
    setEditHelp(null);
    axios
      .patch(serverUrl + "/help/" + editHelp._id, values, {
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
        },
      })
      .then((res) => {
        message.success("Help Updated");
        queryClient.invalidateQueries("help");
      })
      .catch((error) => {
        message.error(error?.response?.data?.message);
      });
  }

  const handleRecord = (rec) => {
    setEditHelp(rec);
    form.setFieldsValue({
      question: rec.question,
      answer: rec.answer,
    });
  };

  const isGuide = (rec) => String(rec._id).startsWith("guide-");

  const videoFor = (rec) =>
    rec.video || (isInstructorFaq(rec) ? INSTRUCTOR_VIDEO : null);

  // Built-in guides first, then FAQs created through the API
  // "Add Instructor" is a built-in guide now, so hide the old saved FAQ for it
  const savedFaqs = ((helps?.isSuccess && helps?.data) || []).filter(
    (f) => !/add.*instructor/i.test(f.question)
  );
  const faqs = [...ADMIN_GUIDES, ...savedFaqs];
  const playing = faqs.find((f) => f._id === playingId);
  const playingVideo = playing && videoFor(playing);

  const iconStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  return (
    <div className="admin-help">
      <style>{`
        .admin-help .yt-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, 130px);
          gap: 14px 10px;
        }
        .admin-help .yt-card { cursor: pointer; }
        .admin-help .yt-thumb {
          position: relative;
          aspect-ratio: 16 / 9;
          border-radius: 6px;
          overflow: hidden;
          background: #063C4E;
        }
        .admin-help .yt-thumb video {
          width: 100%;
          height: 100%;
          object-fit: cover;
          pointer-events: none;
          transition: transform .25s;
        }
        .admin-help .yt-card:hover .yt-thumb video { transform: scale(1.04); }
        .admin-help .yt-thumb-play {
          position: absolute;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #fff;
          font-size: 18px;
          background: rgba(0, 0, 0, .18);
          opacity: 0;
          transition: opacity .2s;
        }
        .admin-help .yt-card:hover .yt-thumb-play { opacity: 1; }
        .admin-help .yt-title {
          font-size: 11px;
          font-weight: 600;
          color: #111827;
          line-height: 1.35;
        }
        .admin-help .yt-desc {
          font-size: 10px;
          color: #6b7280;
          line-height: 1.45;
          display: -webkit-box;
          -webkit-line-clamp: 1;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .admin-help .yt-desc.open { display: block; -webkit-line-clamp: unset; }
        .admin-help .yt-more {
          font-size: 10px;
          font-weight: 500;
          color: #09617E;
          background: none;
          border: 0;
          padding: 0;
          cursor: pointer;
        }
        .admin-help .yt-more:hover { text-decoration: underline; }
        .admin-help .yt-steps {
          margin: 4px 0 0;
          padding-left: 1.1rem;
          font-size: 10px;
          color: #4b5563;
          line-height: 1.5;
        }
        .admin-help .yt-steps li::marker { color: #f97316; font-weight: 600; }
        .admin-help .yt-player {
          width: 100%;
          aspect-ratio: 16 / 9;
          display: block;
          background: #000;
          border-radius: 12px;
        }
        @media (max-width: 480px) {
          .admin-help .yt-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px 10px; }
        }
      `}</style>
      <div className="p-4 sm:p-8">
        <div className="bg-white p-4 sm:p-6 rounded-2xl space-y-4">
          {/* ADD button hidden for now
          <div className="flex justify-end">
            <Button onClick={() => setHelp(true)} type="primary">
              ADD
            </Button>
          </div>
          */}

          {helps?.isLoading ? (
            <div className="flex justify-center py-10">
              <Spin />
            </div>
          ) : faqs.length === 0 ? (
            <Empty description="No help articles yet" />
          ) : (
            <div className="yt-grid">
              {faqs.map((faq) => {
                const video = videoFor(faq);
                return (
                  <div
                    key={faq._id}
                    className="yt-card space-y-2"
                    onClick={() => setPlayingId(faq._id)}
                  >
                    <div className="yt-thumb">
                      {video && (
                        <video
                          muted
                          preload="metadata"
                          src={`${video}#t=1`}
                        />
                      )}
                      <div className="yt-thumb-play">
                        <PlayCircleOutlined />
                      </div>
                    </div>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="yt-title">{faq.question}</div>
                        <div className="yt-desc">{toLines(faq.answer)[0]}</div>
                        {(faq.details || toLines(faq.answer).length > 1) && (
                          <>
                            {expanded.includes(faq._id) && (
                              <ol className="yt-steps">
                                {toLines(faq.details || faq.answer).map((step, i) => (
                                    <li key={i}>{step}</li>
                                  ))}
                              </ol>
                            )}
                            <button
                              type="button"
                              className="yt-more"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleMore(faq._id);
                              }}
                            >
                              {expanded.includes(faq._id)
                                ? "Show less"
                                : "View more"}
                            </button>
                          </>
                        )}
                        {video && (
                          <div>
                            <button
                              type="button"
                              className="yt-more"
                              onClick={(e) => {
                                e.stopPropagation();
                                copyLink(video);
                              }}
                            >
                              <LinkOutlined /> Copy link
                            </button>
                          </div>
                        )}
                      </div>
                      {!isGuide(faq) && (
                        <div
                          className="flex items-center gap-2 shrink-0"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Tooltip title="edit">
                            <Button
                              onClick={() => handleRecord(faq)}
                              type="primary"
                              shape="circle"
                              size="small"
                              icon={<BsPencil style={iconStyle} />}
                            />
                          </Tooltip>
                          <Popconfirm
                            title="Are you sure to delete?"
                            onConfirm={() => deleteHelp(faq._id)}
                            okText="Yes"
                            cancelText="No"
                          >
                            <Button
                              danger
                              shape="circle"
                              size="small"
                              icon={<DeleteOutlined style={iconStyle} />}
                            />
                          </Popconfirm>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ------------------------Player----------------------- */}
      <Modal
        width={900}
        open={!!playing}
        onCancel={() => setPlayingId(null)}
        footer={null}
        destroyOnClose
      >
        {playing && (
          <div className="space-y-3 pt-6">
            {playingVideo ? (
              <video
                controls
                preload="metadata"
                className="yt-player"
                src={playingVideo}
              />
            ) : (
              <div className="text-gray-500">No video for this topic.</div>
            )}
            <div className="yt-title">{playing.question}</div>
            <div className="text-gray-600 whitespace-pre-line">
              {playing.answer}
            </div>
          </div>
        )}
      </Modal>

      {/* ------------------------Create----------------------- */}
      <Modal
        width={800}
        visible={help}
        onOk={() => setHelp(false)}
        onCancel={() => {
          setHelp(false);
          form.resetFields();
        }}
        footer={null}
      >
        <div>
          <div className="py-2">
            <div className="text-2xl font-bold text-black pb-4 capitalize">
              Create Help / Faq
            </div>
          </div>
          {/* ---------------------------form---------------- */}
          <div>
            <Form
              layout="vertical"
              name="create-Help"
              onFinish={createHelp}
              // onFinishFailed={onFinishFailed}
              autoComplete="off"
            >
              <Form.Item
                label="Question"
                name="question"
                rules={[
                  {
                    required: true,
                    message: "Please input question!",
                  },
                ]}
              >
                <Input className="w-full" />
              </Form.Item>
              <Form.Item
                label="Answer"
                name="answer"
                rules={[
                  {
                    required: true,
                    message: "Please input answer!",
                  },
                ]}
              >
                <Input.TextArea className="w-full" />
              </Form.Item>
              <div className="flex justify-end items-center pt-6">
                <div className="inline-flex items-center space-x-2">
                  <Button
                    size="large"
                    type="dark"
                    onClick={() => {
                      setHelp(false);
                      form.resetFields();
                    }}
                  >
                    Cancel
                  </Button>

                  <Button
                    size="large"
                    type="primary"
                    htmlType="submit"
                    className="text-white"
                  >
                    Create
                  </Button>
                </div>
              </div>
            </Form>
          </div>
          {/* ----------------------------------------------- */}
        </div>
      </Modal>
      {/* ----------------------------------------------- */}

      {/* ------------------------Update----------------------- */}
      <Modal
        width={800}
        visible={editHelp}
        onCancel={() => setEditHelp(null)}
        footer={null}
      >
        <div>
          <div className="py-2">
            <div className="text-2xl font-bold text-black pb-4 capitalize">
              Create Help / Faq
            </div>
          </div>
          {/* ---------------------------form---------------- */}
          <div>
            <Form
              layout="vertical"
              name="create-department"
              onFinish={updateHelp}
              // onFinishFailed={onFinishFailed}
              initialValues={editHelp}
              autoComplete="off"
              form={form}
            >
              <Form.Item
                label="Question"
                name="question"
                rules={[
                  {
                    required: true,
                    message: "Please input question!",
                  },
                ]}
              >
                <Input className="w-full" />
              </Form.Item>
              <Form.Item
                label="Answer"
                name="answer"
                rules={[
                  {
                    required: true,
                    message: "Please input answer!",
                  },
                ]}
              >
                <Input.TextArea className="w-full" />
              </Form.Item>
              <div className="flex justify-end items-center pt-6">
                <div className="inline-flex items-center space-x-2">
                  <Button
                    size="large"
                    type="dark"
                    onClick={() => setEditHelp(null)}
                  >
                    Cancel
                  </Button>

                  <Button
                    size="large"
                    type="primary"
                    htmlType="submit"
                    className="text-white"
                  >
                    Update
                  </Button>
                </div>
              </div>
            </Form>
          </div>
          {/* ----------------------------------------------- */}
        </div>
      </Modal>
      {/* ----------------------------------------------- */}
    </div>
  );
}
